import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FiBriefcase, FiFileText, FiExternalLink, FiCheckCircle, FiClock, FiXCircle } from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import EmptyState from "../components/EmptyState";
import BreadcrumbTrail from "../components/BreadcrumbTrail";
import ResponsiveModal from "../components/ResponsiveModal";
import { useAuth } from "../contexts/AuthContext";
import { audienceLabel, formatDate, statusLabel, statusTone } from "../utils/format";

const TABS = [
  { id: "overview", label: "Visão geral" },
  { id: "segmentation", label: "Segmentação" },
  { id: "products", label: "Produtos" },
  { id: "ads", label: "Anúncios" },
  { id: "documents", label: "Documentos" },
];

const SectionTitle = ({ title, subtitle = "Painel" }) => (
  <div
    style={{
      marginBottom: "1.1rem",
      paddingBottom: "0.8rem",
      borderBottom: "1px solid #e2e8f0",
    }}
  >
    <span
      style={{
        display: "inline-block",
        fontSize: "0.72rem",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#64748b",
        fontWeight: 700,
        marginBottom: "0.3rem",
      }}
    >
      {subtitle}
    </span>
    <h3
      style={{
        margin: 0,
        fontSize: "1.16rem",
        fontWeight: 700,
        color: "#0f172a",
        lineHeight: 1.2,
      }}
    >
      {title}
    </h3>
    <div
      style={{
        marginTop: "0.55rem",
        height: "2px",
        width: "72px",
        borderRadius: "999px",
        background: "linear-gradient(90deg, #16a34a 0%, #86efac 100%)",
      }}
    />
  </div>
);

const PaginationControls = ({ currentPage, totalPages, onChangePage }) => {
  if (!totalPages || totalPages <= 1) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", marginTop: "1rem" }}>
      <button
        type="button"
        className="button button--ghost"
        onClick={() => onChangePage(Math.max(1, currentPage - 1))}
        disabled={currentPage <= 1}
      >
        Anterior
      </button>
      <span className="muted">
        Página {currentPage} de {totalPages}
      </span>
      <button
        type="button"
        className="button button--ghost"
        onClick={() => onChangePage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage >= totalPages}
      >
        Próxima
      </button>
    </div>
  );
};

const CompanyDetailsPage = () => {
  const { companyId } = useParams();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsMeta, setProductsMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage: 10,
  });
  const [ads, setAds] = useState([]);
  const [adsMeta, setAdsMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage: 10,
  });
  const [tabCounts, setTabCounts] = useState({
    overview: 0,
    segmentation: 0,
    products: 0,
    ads: 0,
    documents: 0,
  });
  const [documents, setDocuments] = useState([]);
  const [companyCategories, setCompanyCategories] = useState([]);
  const [companyRegions, setCompanyRegions] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [companyFormError, setCompanyFormError] = useState("");
  const [companyForm, setCompanyForm] = useState({
    nomeFantasia: "",
    razaoSocial: "",
    cnpj: "",
    statusVerificacao: "PENDENTE",
    emailCorporativo: "",
    whatsapp: "",
    siteOficial: "",
    enderecoCidade: "",
    enderecoEstado: "",
  });

  const [productsPage, setProductsPage] = useState(1);
  const [productsItemsPerPage, setProductsItemsPerPage] = useState(10);
  const [productsQuery, setProductsQuery] = useState("");
  const [productsUrgency, setProductsUrgency] = useState("");

  const [adsPage, setAdsPage] = useState(1);
  const [adsItemsPerPage, setAdsItemsPerPage] = useState(10);
  const [adsQuery, setAdsQuery] = useState("");
  const [adsStatus, setAdsStatus] = useState("");
  const [adsAudience, setAdsAudience] = useState("");

  const canAccessCompanies = ["POWER_USER", "ADMIN"].includes(user?.role);

  useEffect(() => {
    const fetchData = async () => {
      if (!canAccessCompanies || !companyId) return;

      setLoading(true);
      try {
        const response = await axiosInstance.get(`/companies/${companyId}/details`, {
          params: {
            productsPage,
            productsItemsPerPage,
            productsQuery,
            productsUrgency,
            adsPage,
            adsItemsPerPage,
            adsQuery,
            adsStatus,
            adsAudience,
          },
        });
        const details = response?.data?.data;

        setCompany(details?.company || null);
        setProducts(details?.products?.data || []);
        setProductsMeta({
          currentPage: details?.products?.currentPage || 1,
          totalPages: details?.products?.totalPages || 1,
          totalCount: details?.products?.totalCount || 0,
          itemsPerPage: details?.products?.itemsPerPage || productsItemsPerPage,
        });
        setAds(details?.ads?.data || []);
        setAdsMeta({
          currentPage: details?.ads?.currentPage || 1,
          totalPages: details?.ads?.totalPages || 1,
          totalCount: details?.ads?.totalCount || 0,
          itemsPerPage: details?.ads?.itemsPerPage || adsItemsPerPage,
        });
        setDocuments(details?.documents || []);
        setCompanyCategories(details?.categories || []);
        setCompanyRegions(details?.regions || []);
        setTabCounts(
          details?.tabCounts || {
            overview: details?.company ? 1 : 0,
            segmentation: (details?.totals?.categories || 0) + (details?.totals?.regions || 0),
            products: details?.totals?.products || 0,
            ads: details?.totals?.ads || 0,
            documents: details?.totals?.documents || 0,
          }
        );
      } catch (error) {
        setCompany(null);
        setProducts([]);
        setProductsMeta({ currentPage: 1, totalPages: 1, totalCount: 0, itemsPerPage: productsItemsPerPage });
        setAds([]);
        setAdsMeta({ currentPage: 1, totalPages: 1, totalCount: 0, itemsPerPage: adsItemsPerPage });
        setDocuments([]);
        setCompanyCategories([]);
        setCompanyRegions([]);
        setTabCounts({ overview: 0, segmentation: 0, products: 0, ads: 0, documents: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    canAccessCompanies,
    companyId,
    productsPage,
    productsItemsPerPage,
    productsQuery,
    productsUrgency,
    adsPage,
    adsItemsPerPage,
    adsQuery,
    adsStatus,
    adsAudience,
  ]);

  const selectedCategoryLabels = useMemo(() => {
    return companyCategories
      .map((item) => item?.Category?.nome || item?.category?.nome)
      .filter(Boolean);
  }, [companyCategories]);

  const selectedRegionLabels = useMemo(() => {
    return companyRegions
      .map((item) => {
        const region = item?.Region || item?.region;
        if (!region) return null;
        return `${region.stateCode}${region.city ? ` - ${region.city}` : ""}`;
      })
      .filter(Boolean);
  }, [companyRegions]);

  const breadcrumbItems = useMemo(
    () => [
      { label: "Empresas", to: "/empresas" },
      {
        label: company?.nomeFantasia || company?.razaoSocial || "Detalhes do cliente",
      },
    ],
    [company]
  );

  const getTabButtonStyle = (isActive) => ({
    padding: "0.42rem 0.82rem",
    borderRadius: "999px",
    fontSize: "0.82rem",
    fontWeight: 600,
    lineHeight: 1.1,
    border: isActive ? "1px solid #22c55e" : "1px solid #dbe4ef",
    background: isActive ? "#ecfdf3" : "#ffffff",
    color: isActive ? "#166534" : "var(--color-text-muted, #64748b)",
    boxShadow: "none",
    transition: "all 160ms ease",
  });

  const openEditModal = () => {
    setCompanyFormError("");
    setCompanyForm({
      nomeFantasia: company?.nomeFantasia || company?.fantasyName || "",
      razaoSocial: company?.razaoSocial || company?.name || "",
      cnpj: company?.cnpj || "",
      statusVerificacao: company?.statusVerificacao || "PENDENTE",
      emailCorporativo: company?.emailCorporativo || company?.email || "",
      whatsapp: company?.whatsapp || "",
      siteOficial: company?.siteOficial || "",
      enderecoCidade: company?.enderecoCidade || "",
      enderecoEstado: company?.enderecoEstado || "",
    });
    setIsEditModalOpen(true);
  };

  const handleCompanyFieldChange = (event) => {
    const { name, value } = event.target;
    setCompanyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCompany = async () => {
    if (!company?.id) return;

    setIsSavingCompany(true);
    setCompanyFormError("");

    try {
      const payload = {
        nomeFantasia: companyForm.nomeFantasia.trim() || null,
        razaoSocial: companyForm.razaoSocial.trim() || null,
        cnpj: companyForm.cnpj.trim() || null,
        statusVerificacao: companyForm.statusVerificacao,
        emailCorporativo: companyForm.emailCorporativo.trim() || null,
        whatsapp: companyForm.whatsapp.trim() || null,
        siteOficial: companyForm.siteOficial.trim() || null,
        enderecoCidade: companyForm.enderecoCidade.trim() || null,
        enderecoEstado: companyForm.enderecoEstado.trim() || null,
      };

      const response = await axiosInstance.put(`/companies/${company.id}`, payload);
      const updatedCompany = response?.data?.data || payload;

      setCompany((prev) => ({ ...prev, ...updatedCompany }));
      setIsEditModalOpen(false);
    } catch (error) {
      setCompanyFormError("Não foi possível salvar os dados do cliente. Tente novamente.");
    } finally {
      setIsSavingCompany(false);
    }
  };

  if (!canAccessCompanies) {
    return (
      <EmptyState
        icon={FiBriefcase}
        title="Acesso restrito"
        description="A visualização de clientes está disponível apenas para usuários POWER_USER ou ADMIN."
      />
    );
  }

  if (loading) {
    return <div className="muted">Carregando cliente...</div>;
  }

  if (!company) {
    return (
      <EmptyState
        icon={FiBriefcase}
        title="Cliente não encontrado"
        description="Não foi possível encontrar os dados desse cliente."
      />
    );
  }

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div style={{ width: "100%" }}>
          <BreadcrumbTrail items={breadcrumbItems} />
          <div
            style={{
              marginTop: "0.5rem",
              padding: "0.9rem 1rem",
              borderRadius: "0.8rem",
              border: "1px solid #e2e8f0",
              background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <h2 style={{ margin: 0, fontSize: "1.3rem", color: "#0f172a" }}>
              {company?.nomeFantasia || company?.razaoSocial || "Detalhes do cliente"}
            </h2>
            <p style={{ margin: "0.3rem 0 0", color: "#64748b", fontSize: "0.92rem" }}>
              Acompanhe dados gerais, segmentação, produtos, anúncios e documentos em um só lugar.
            </p>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.4rem",
          flexWrap: "wrap",
          width: "fit-content",
          maxWidth: "100%",
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            style={getTabButtonStyle(activeTab === tab.id)}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label} ({tabCounts[tab.id] || 0})
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="section-card">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
            <SectionTitle title="Dados do cliente" subtitle="Visão geral" />
            <button
              type="button"
              className="button button--ghost"
              onClick={openEditModal}
              style={{ padding: "0.55rem 0.95rem", borderRadius: "0.6rem", fontWeight: 600 }}
            >
              Editar dados do cliente
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
            <div><strong>Razão social:</strong><br />{company?.razaoSocial || company?.name || "-"}</div>
            <div><strong>Nome fantasia:</strong><br />{company?.nomeFantasia || company?.fantasyName || "-"}</div>
            <div><strong>CNPJ:</strong><br />{company?.cnpj || "-"}</div>
            <div><strong>Email:</strong><br />{company?.emailCorporativo || company?.email || "-"}</div>
            <div><strong>WhatsApp:</strong><br />{company?.whatsapp || "-"}</div>
            <div><strong>Status:</strong><br />{company?.statusVerificacao || "-"}</div>
            <div><strong>Criado em:</strong><br />{formatDate(company?.createdAt)}</div>
            <div><strong>Atualizado em:</strong><br />{formatDate(company?.updatedAt)}</div>
            <div><strong>Cidade/UF:</strong><br />{[company?.enderecoCidade, company?.enderecoEstado].filter(Boolean).join("/") || "-"}</div>
            <div><strong>Site:</strong><br />{company?.siteOficial || "-"}</div>
          </div>
        </div>
      )}

      {activeTab === "segmentation" && (
        <div className="section-card">
          <SectionTitle title="Segmentação" subtitle="Configurações" />

          <div style={{ marginBottom: "1rem" }}>
            <h4 style={{ margin: "0 0 0.5rem 0" }}>Categorias</h4>
            {selectedCategoryLabels.length === 0 ? (
              <div className="muted">Nenhuma categoria vinculada.</div>
            ) : (
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {selectedCategoryLabels.map((label) => (
                  <span key={label} className="badge">{label}</span>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 style={{ margin: "0 0 0.5rem 0" }}>Regiões</h4>
            {selectedRegionLabels.length === 0 ? (
              <div className="muted">Nenhuma região vinculada.</div>
            ) : (
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                {selectedRegionLabels.map((label) => (
                  <span key={label} className="badge">{label}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "products" && (
        <div className="section-card">
          <SectionTitle title="Produtos" subtitle="Catálogo" />
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
            <input
              className="input"
              type="text"
              placeholder="Buscar produto por título"
              value={productsQuery}
              onChange={(event) => {
                setProductsQuery(event.target.value);
                setProductsPage(1);
              }}
            />
            <select
              className="select"
              value={productsUrgency}
              onChange={(event) => {
                setProductsUrgency(event.target.value);
                setProductsPage(1);
              }}
            >
              <option value="">Urgência: todas</option>
              <option value="urgent">Somente urgentes</option>
              <option value="normal">Somente sem urgência</option>
            </select>
            <select
              className="select"
              value={productsItemsPerPage}
              onChange={(event) => {
                setProductsItemsPerPage(Number(event.target.value));
                setProductsPage(1);
              }}
            >
              <option value={10}>10 por pagina</option>
              <option value={20}>20 por pagina</option>
              <option value={50}>50 por pagina</option>
              <option value={-1}>Todos</option>
            </select>
          </div>
          <div className="muted" style={{ marginBottom: "0.75rem" }}>
            {productsMeta.totalCount} produto(s) encontrado(s)
          </div>
          {products.length === 0 ? (
            <div className="muted">Nenhum produto encontrado para este cliente.</div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Condição</th>
                    <th>Urgência</th>
                    <th>Atualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id}>
                      <td>{product?.title || "-"}</td>
                      <td>{product?.priceCondition || "-"}</td>
                      <td>{product?.hasUrgency ? "Sim" : "Nao"}</td>
                      <td>{formatDate(product?.updatedAt || product?.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls
                currentPage={productsMeta.currentPage}
                totalPages={productsMeta.totalPages}
                onChangePage={setProductsPage}
              />
            </>
          )}
        </div>
      )}

      {activeTab === "ads" && (
        <div className="section-card">
          <SectionTitle title="Anúncios" subtitle="Mídia" />
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "0.75rem", marginBottom: "1rem" }}>
            <input
              className="input"
              type="text"
              placeholder="Buscar anúncio por título"
              value={adsQuery}
              onChange={(event) => {
                setAdsQuery(event.target.value);
                setAdsPage(1);
              }}
            />
            <select
              className="select"
              value={adsStatus}
              onChange={(event) => {
                setAdsStatus(event.target.value);
                setAdsPage(1);
              }}
            >
              <option value="">Status: todos</option>
              <option value="PENDENTE">Pendente</option>
              <option value="APROVADO">Aprovado</option>
              <option value="REJEITADO">Rejeitado</option>
              <option value="PAUSADO">Pausado</option>
              <option value="EXPIRADO">Expirado</option>
            </select>
            <select
              className="select"
              value={adsAudience}
              onChange={(event) => {
                setAdsAudience(event.target.value);
                setAdsPage(1);
              }}
            >
              <option value="">Público: todos</option>
              <option value="ATACADO">Atacado</option>
              <option value="VAREJO">Varejo</option>
              <option value="FOOD_SERVICE">Food Service</option>
            </select>
            <select
              className="select"
              value={adsItemsPerPage}
              onChange={(event) => {
                setAdsItemsPerPage(Number(event.target.value));
                setAdsPage(1);
              }}
            >
              <option value={10}>10 por pagina</option>
              <option value={20}>20 por pagina</option>
              <option value={50}>50 por pagina</option>
              <option value={-1}>Todos</option>
            </select>
          </div>
          <div className="muted" style={{ marginBottom: "0.75rem" }}>
            {adsMeta.totalCount} anúncio(s) encontrado(s)
          </div>
          {ads.length === 0 ? (
            <div className="muted">Nenhum anúncio encontrado para este cliente.</div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Público</th>
                    <th>Status</th>
                    <th>Visualizações</th>
                    <th>Cliques</th>
                    <th>Atualizado</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map((ad) => (
                    <tr key={ad.id}>
                      <td>{ad?.title || "-"}</td>
                      <td>{audienceLabel(ad?.targetAudience)}</td>
                      <td>
                        <span className="badge" data-tone={statusTone(ad?.status)}>
                          {statusLabel(ad?.status)}
                        </span>
                      </td>
                      <td>{ad?.viewsCount || 0}</td>
                      <td>{ad?.clicksCount || 0}</td>
                      <td>{formatDate(ad?.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <PaginationControls
                currentPage={adsMeta.currentPage}
                totalPages={adsMeta.totalPages}
                onChangePage={setAdsPage}
              />
            </>
          )}
        </div>
      )}

      {activeTab === "documents" && (
        <div className="section-card">
          <SectionTitle title="Documentos" subtitle="Compliance" />
          {documents.length === 0 ? (
            <div className="muted">Nenhum documento enviado por este cliente.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Arquivo</th>
                  <th>Status</th>
                  <th>Enviado em</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const statusMap = {
                    APROVADO: { icon: <FiCheckCircle size={13} />, label: "Aprovado", color: "#166534", bg: "#dcfce7" },
                    PENDENTE: { icon: <FiClock size={13} />, label: "Em análise", color: "#92400e", bg: "#fef3c7" },
                    REJEITADO: { icon: <FiXCircle size={13} />, label: "Rejeitado", color: "#991b1b", bg: "#fee2e2" },
                  };
                  const statusInfo = statusMap[doc?.status];
                  return (
                    <tr key={doc.id}>
                      <td>
                        <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>
                          {doc?.type || "-"}
                        </span>
                      </td>
                      <td>
                        {doc?.fileUrl ? (
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                              backgroundColor: "#f8fafc",
                              color: "#334155",
                              fontSize: "0.82rem",
                              fontWeight: 600,
                              textDecoration: "none",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f1f5f9"}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f8fafc"}
                          >
                            <FiFileText size={14} style={{ color: "#64748b" }} />
                            Ver arquivo
                            <FiExternalLink size={12} style={{ color: "#94a3b8" }} />
                          </a>
                        ) : (
                          <span style={{ color: "#94a3b8", fontSize: "0.82rem" }}>Não enviado</span>
                        )}
                      </td>
                      <td>
                        {statusInfo ? (
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "3px 10px",
                            borderRadius: "999px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: statusInfo.color,
                            backgroundColor: statusInfo.bg,
                          }}>
                            {statusInfo.icon}
                            {statusInfo.label}
                          </span>
                        ) : "-"}
                      </td>
                      <td style={{ color: "#64748b", fontSize: "0.82rem" }}>{formatDate(doc?.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <ResponsiveModal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isSavingCompany) setIsEditModalOpen(false);
        }}
        title="Editar dados do cliente"
        subtitle="empresa"
        size="lg"
        primaryAction={{
          label: isSavingCompany ? "Salvando..." : "Salvar alterações",
          onClick: handleSaveCompany,
          disabled: isSavingCompany,
        }}
        secondaryAction={{
          label: "Cancelar",
          onClick: () => setIsEditModalOpen(false),
          disabled: isSavingCompany,
        }}
      >
        {companyFormError && (
          <div
            style={{
              backgroundColor: "#fee2e2",
              color: "#ef4444",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.92rem",
            }}
          >
            {companyFormError}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.9rem" }}>
          <div>
            <label htmlFor="nomeFantasia" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              Nome fantasia
            </label>
            <input id="nomeFantasia" name="nomeFantasia" className="input" value={companyForm.nomeFantasia} onChange={handleCompanyFieldChange} />
          </div>

          <div>
            <label htmlFor="razaoSocial" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              Razão social
            </label>
            <input id="razaoSocial" name="razaoSocial" className="input" value={companyForm.razaoSocial} onChange={handleCompanyFieldChange} />
          </div>

          <div>
            <label htmlFor="cnpj" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              CNPJ
            </label>
            <input id="cnpj" name="cnpj" className="input" value={companyForm.cnpj} onChange={handleCompanyFieldChange} />
          </div>

          <div>
            <label htmlFor="statusVerificacao" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              Status
            </label>
            <select
              id="statusVerificacao"
              name="statusVerificacao"
              className="select"
              value={companyForm.statusVerificacao}
              onChange={handleCompanyFieldChange}
              style={{ width: "100%" }}
            >
              <option value="PENDENTE">Pendente</option>
              <option value="APROVADO">Aprovado</option>
              <option value="REJEITADO">Rejeitado</option>
            </select>
          </div>

          <div>
            <label htmlFor="emailCorporativo" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              E-mail corporativo
            </label>
            <input id="emailCorporativo" name="emailCorporativo" type="email" className="input" value={companyForm.emailCorporativo} onChange={handleCompanyFieldChange} />
          </div>

          <div>
            <label htmlFor="whatsapp" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              WhatsApp
            </label>
            <input id="whatsapp" name="whatsapp" className="input" value={companyForm.whatsapp} onChange={handleCompanyFieldChange} />
          </div>

          <div>
            <label htmlFor="siteOficial" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              Site oficial
            </label>
            <input id="siteOficial" name="siteOficial" className="input" value={companyForm.siteOficial} onChange={handleCompanyFieldChange} />
          </div>

          <div>
            <label htmlFor="enderecoCidade" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              Cidade
            </label>
            <input id="enderecoCidade" name="enderecoCidade" className="input" value={companyForm.enderecoCidade} onChange={handleCompanyFieldChange} />
          </div>

          <div>
            <label htmlFor="enderecoEstado" style={{ fontWeight: 600, display: "block", marginBottom: "0.35rem" }}>
              Estado (UF)
            </label>
            <input id="enderecoEstado" name="enderecoEstado" className="input" value={companyForm.enderecoEstado} onChange={handleCompanyFieldChange} maxLength={2} />
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default CompanyDetailsPage;
